import type { ReactNode } from 'react';
import { UMIcon, UMLegendItem, UMR } from '../../../urbanmap/_components';
import { densityLegendEntries } from './densityLegend';
import { DensityLegendMenu } from './DensityLegendMenu';
import { PIPE_COLOR } from './networkDisplay';
import type { useDensity } from './useDensity';
import { LegendSelect } from './LegendSelect';

export const STALE_DENSITY_LABEL = 'Densités à recalculer';

export function DensityStaleWarning() {
  return <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, lineHeight: '18px', color: UMR.warning }}>
    <UMIcon name="warning" size={16} color={UMR.warning} />{STALE_DENSITY_LABEL}
  </div>;
}

export function NetworkLegend({ density, visible = true, onVisibilityChange, context = 'selection', existingLegend, onEditLegend }: {
  density: ReturnType<typeof useDensity>; visible?: boolean; onVisibilityChange?: (visible: boolean) => void;
  context?: 'selection' | 'layers'; existingLegend?: ReactNode; onEditLegend?: () => void;
}) {
  const visibilityLabel = `${visible ? 'Masquer' : 'Afficher'} les canalisations`;
  const layers = context === 'layers';
  const showDensity = density.display === 'density' && !!density.result;
  return <div aria-label="Affichage des canalisations" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {/* The reference eye row lacks pressed state / keyboard; the wrapper adds both. */}
    <div role="button" tabIndex={0} aria-label={visibilityLabel} title={`${visibilityLabel} de la sélection`}
      aria-pressed={visible} onClick={() => onVisibilityChange?.(!visible)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault(); onVisibilityChange?.(!visible);
        }
      }} style={{ cursor: 'pointer' }}>
      <UMLegendItem label={layers ? 'Canalisations RCU/RFU' : 'Canalisations'}
        kind="visibility" color={UMR.map.pipe} disabled={!visible} />
    </div>
    {/* Stable wrapper: the select must not remount (and lose focus) when the ⋮ menu appears. */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <LegendSelect value={density.display} hasDensity={!!density.result} onChange={density.setDisplay} />
      </div>
      {showDensity && onEditLegend
        && <DensityLegendMenu onEdit={onEditLegend} label={layers ? 'Canalisations RCU/RFU' : 'Canalisations'} />}
    </div>
    {density.stale && <DensityStaleWarning />}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    {existingLegend}
    {showDensity
      ? <div aria-label="Légende de densité linéaire" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {densityLegendEntries(density.legend).map((c) =>
          <UMLegendItem key={c.label} label={c.label} color={c.color} kind="line" sub disabled={!visible} />)}
      </div>
      : <div aria-label="Légende des canalisations">
        <UMLegendItem label={layers ? 'Réseaux de la sélection' : 'Tracé de la sélection'}
          color={PIPE_COLOR} kind="line" sub disabled={!visible} />
      </div>}
    </div>
  </div>;
}
